import type { PlanAppointmentsRequest } from 'generated';

import type { ConnectAppointmentGroupingPayload } from 'RestAPIs/activities';

/**
 * An action a confirmation popover is waiting on: the payload to send once the
 * user picks an occurrence option, plus the columns to scope the loading state
 * to while it runs. Stamped with the popover it was registered for.
 */
export interface PendingConfirmation {
  popoverId: string;
  payload: PlanAppointmentsRequest | ConnectAppointmentGroupingPayload;
  columnIds: string[];
}

/**
 * The pending action, but only if it belongs to the popover currently open.
 *
 * Both are single slots, and the popover can close without being answered — a
 * later drop resets it, for one. Without the match the next popover to open
 * would find the abandoned action still there and send it.
 *
 * Note this cannot tell a drop's popover from a context-menu popover for the
 * same card: they share an id. Whether the action was scoped to columns by a
 * drop is a separate question, answered by `isDragAction`.
 */
const resolvePendingConfirmation = (
  pending: PendingConfirmation | null,
  openPopoverId: string
): PendingConfirmation | null =>
  pending?.popoverId === openPopoverId ? pending : null;

/**
 * What answering a confirmation popover should do. `none` means "send nothing":
 * the popover is being answered but no action belongs to it.
 */
export type ConfirmationAction =
  | {
      kind: 'unassign';
      /** Absent for a board-wide unassign, which keeps the full-screen overlay. */
      columnIds: string[] | undefined;
      /** Whether this unassign is responsible for releasing the frozen drop. */
      releasesFrozenDrop: boolean;
    }
  | { kind: 'plan'; payload: PlanAppointmentsRequest; columnIds: string[] }
  | { kind: 'connect'; payload: ConnectAppointmentGroupingPayload }
  | { kind: 'none' };

/**
 * Decides what a popover confirmation resolves to, given the action registered
 * for it and how the popover was opened. Pure: the caller performs the action.
 *
 * `isDragAction` distinguishes a popover opened by a drop from one opened by the
 * context menu for the same card — the two share an id, so the action's own
 * stamp cannot tell them apart (see `resolvePendingConfirmation`).
 */
export const routeConfirmation = ({
  pending,
  openPopoverId,
  isUnassignment,
  isDragAction
}: {
  pending: PendingConfirmation | null;
  openPopoverId: string;
  isUnassignment?: boolean;
  isDragAction?: boolean;
}): ConfirmationAction => {
  const resolved = resolvePendingConfirmation(pending, openPopoverId);

  // Checked before the `none` case below, not after: an unassign legitimately has
  // no registered action, because a context-menu unassign never registers one.
  if (isUnassignment) {
    // Columns and the freeze apply only to a drop. A context-menu unassign is
    // board-wide and must not inherit the columns of a drop whose action happens
    // to be sitting in the slot.
    const isDrop = !!isDragAction;
    return {
      kind: 'unassign',
      columnIds: isDrop ? resolved?.columnIds : undefined,
      releasesFrozenDrop: isDrop
    };
  }

  // Never registered under this id, or cleared while the popover sat open. The
  // caller must send nothing: with no payload there is no appointment grouping to
  // connect, and the request could only be rejected.
  if (!resolved) return { kind: 'none' };

  if ('appointmentOccurrences' in resolved.payload) {
    return {
      kind: 'plan',
      payload: resolved.payload as PlanAppointmentsRequest,
      columnIds: resolved.columnIds
    };
  }

  return {
    kind: 'connect',
    payload: resolved.payload as ConnectAppointmentGroupingPayload
  };
};
